import { ReplaySubject } from 'rxjs';

import { IOC_TYPES } from '../../context/ioc.types';
import { container } from '../../context/module';
import { DropboxFile } from '../../models/dropbox-file';
import { FileUploadRequestMetadata } from '../../models/file-upload-request-metadata';
import { GoogleDriveFile } from '../../models/google-drive-file';
import { Logger } from '../../models/logger';
import { UploadInProgressFile, UploadInProgressFileFactory } from '../../models/upload-in-progress-file';
import { GAService } from '../../services/analytics.service';
import { ApiConfig } from '../../services/api-config';
import { FileUploadService } from '../../services/file-upload.service';
import { LightHttp } from '../../services/light-http.service';
import { ProcessorResolverService } from '../../services/processor-resolver.service';
import { UIContext } from '../../services/ui-context';
import { FileSelectEvent } from '../../types/file-upload.types';
import { GAContants } from '../../types/ga.types';
import { OperationTypes, OriginalInputFile } from '../../types/types';
import { filter } from '../../utils/lodash-min';
import { RouteUtils } from '../../utils/route.utils';


export type SupportedFileTypes = DropboxFile | GoogleDriveFile | File;

export interface FileSelectHandleRequest<T> {
    files: T[];
    fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[];
    fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[];
}

export class FileUploadManager {
    private fileUploadService: FileUploadService;
    private processorResolverService: ProcessorResolverService;
    private uiContext: UIContext;
    private gaService: GAService;

    private apiConfig: ApiConfig;
    private lightHttp: LightHttp;

    private _fileListChanged: ReplaySubject<{
        fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[],
        fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[]
    }> = new ReplaySubject(1);
    public readonly fileListChanged = this._fileListChanged.asObservable();

    constructor(
        private fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[],
        private fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[],
        private props: any,
        private logger: Logger,
        private fromType: string[],
        private toType: string,
        private initializeStateUpdateFn: () => void,
        private updateSidePanelFileUploads: () => void,
    ) {
        this.fileUploadService = container.get(IOC_TYPES.FileUploadService);
        this.processorResolverService = container.get(IOC_TYPES.ProcessorResolverService);
        this.uiContext = container.get(IOC_TYPES.UIContext);
        this.gaService = container.get(IOC_TYPES.GAService);
        this.apiConfig = container.get(IOC_TYPES.ApiConfig);
        this.lightHttp = container.get(IOC_TYPES.LightHttp);
    }

    private notifyFileListChanged() {
        this._fileListChanged.next({
            fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles,
            fileWithMetadataList: this.fileWithMetadataList,
        });
    }

    async fileSelected(event: FileSelectEvent) {
        if (event && (event.target || event.files && event.files.length > 0)) {
            this.initializeStateUpdateFn();
        }
        this.fileUploadService.fileSelected();
        this.uiContext.successToast('Preparing To Upload', '');
        let dropboxFiles: any[] = filter(event.files, f => f instanceof DropboxFile);
        let driverFiles: any[] = filter(event.files, f => f instanceof GoogleDriveFile);
        let files: any[] = filter(event.files, f => f instanceof File);

        // @Deprecated
        // if (this.offlineTaskService.getOfflineMode()) {
        //     try {
        //         const capability = this.capabilityService.getConverters().find(c => c.id == RouteUtils.getPathFromProps(this.props));
        //         const canProcessOffline = capability.availability.offline;
        //         if (!canProcessOffline) {
        //             this.uiContext.showErrorDialog(
        //                 { title: 'Error', text: `Offline mode is not supported for this feature. Please turn it off and reload the page.` });
        //         }
        //     } catch (e) {
        //         console.error(e);
        //     }
        // }
        // console.log(dropboxFiles, driverFiles, files);

        const failToUploadFileNames = [];

        if (driverFiles.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(driverFiles.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            driverFiles = driverFiles.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (dropboxFiles.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(dropboxFiles.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            dropboxFiles = dropboxFiles.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (files.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(files.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            files = files.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (failToUploadFileNames.length > 0) {
            // TODO must convert this.fileUploadService.maxFileSizeInBytes to readable bytes for Preact use state too
            this.uiContext.showErrorDialog({
                title: 'File Size Limit Exceeded!', html: `<div style="text-align: left;">Failed to upload <ul><li>${failToUploadFileNames.join('</li><li>')}</li></ul>
              Max file size limit is ${this.fileUploadService.maxFileSizeInBytes}.</div>`
            });
        }

        // file.size dropbox.size
        try {
            /// TODO loadFeaturePageDataForProps Note tested in Peact
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            for (const file of [...dropboxFiles, ...driverFiles, ...files]) {
                const fakeUploadInProgress = await UploadInProgressFileFactory.newUploadInProgressFile(
                    file,
                    new FileUploadRequestMetadata([], file),
                    uniqueProcessType,
                );
                this.fakeUploadInProgressFileForSelectedFiles.push(fakeUploadInProgress);
                this.notifyFileListChanged();
            }


            await this.dropboxFileSelected({
                files: dropboxFiles,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });
            await this.googleDriveFileSelected({
                files: driverFiles,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });
            await this.multiFileUploadEvent({
                files: files,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });

            Promise.all(this.fileWithMetadataList.map(fml => fml.whenComplete())).then(
                () => {
                    this.updateSidePanelFileUploads();
                    this.notifyFileListChanged();
                },
            ).catch(console.error);
        } catch (e) {
            this.logger.error({ error: e, message: 'File upload failed.' });
            this.uiContext.handleError(e);
        }
    }

    async dropboxFileSelected(request: FileSelectHandleRequest<DropboxFile>): Promise<any> {
        if (request.files.length > 0) {
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            const metadataList: FileUploadRequestMetadata[] = [];
            const currentRequestMetadata: UploadInProgressFile<DropboxFile>[] = [];
            for (const file of request.files) {
                currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                    file,
                    null,
                    uniqueProcessType,
                ));
            }
            request.fileWithMetadataList.push(...currentRequestMetadata);
            this.notifyFileListChanged();
            console.log("Notify called without metadata")
            
            for (const currentRequest of currentRequestMetadata) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['SELECT-DIALOG'],
                    GAContants.EventAction['FILE-SELECTED'],
                    GAContants.EventLabel.SELECT,
                    `drive-${currentRequest.file.name}-${currentRequest.file.size}`,
                );
                const metadata = await this.processorResolverService.generateFileUploadRequestMetadata(currentRequest.file, this.fromType, this.toType, OperationTypes.UPLOAD);
                metadataList.push(metadata);
                currentRequest.setMetadata(metadata)
            }

            this.notifyFileListChanged();

            request.files.forEach(f => {
                const uploadCompletedFileIndex = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                if (uploadCompletedFileIndex > -1) {
                    request.fakeUploadInProgressFileForSelectedFiles.splice(uploadCompletedFileIndex, 1);
                    this.notifyFileListChanged();
                }
            });

            const promiseArray = [];
            for (let i = 0; i < currentRequestMetadata.length; i++) {
                const fileWithMetadata = currentRequestMetadata[i];
                promiseArray.push(new Promise<void>((resolve, reject) => {
                    const observerObject = {
                        next: x => {
                            fileWithMetadata.setProgress(x);
                            // fileUpload.progress = x;
                        },
                        error: err => {
                            this.logger.error({ message: `Error uploading file (Dropbox).`, error: err });
                            fileWithMetadata.setError();
                        },
                        complete: () => {
                            this.logger.trace7('complete notification');
                            fileWithMetadata.setComplete();
                        },
                    };
                    this.fileUploadService.uploadFileFromURL(fileWithMetadata, observerObject)
                        .then((originalInputFile: OriginalInputFile) => {
                            fileWithMetadata.setResponse(originalInputFile);
                            this.logger.trace7({ message: `Dropbox file upload success.`, data: originalInputFile });
                            resolve();
                        })
                        .catch((e) => reject(e));
                }));
            }

            await Promise.all(promiseArray);
        }
    }

    async googleDriveFileSelected(request: FileSelectHandleRequest<GoogleDriveFile>) {
        if (request.files.length > 0) {
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            this.logger.trace7({ message: 'Goolge drive file selected.', data: request.files });
            // this.multiFileUploadEvent({ files: files });
            const metadataList: FileUploadRequestMetadata[] = [];
            const currentRequestMetadata: UploadInProgressFile<any>[] = [];

            for (const file of request.files) {
                currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                    file,
                    null,
                    uniqueProcessType,
                ));
            }
            request.fileWithMetadataList.push(...currentRequestMetadata);
            this.notifyFileListChanged();
            console.log("Notify called without metadata")

            for (const currentRequest of currentRequestMetadata) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['SELECT-DIALOG'],
                    GAContants.EventAction['FILE-SELECTED'],
                    GAContants.EventLabel.SELECT,
                    `drive-${currentRequest.file.name}-${currentRequest.file.size}`,
                );
                const metadata = await this.processorResolverService.generateFileUploadRequestMetadata(currentRequest.file, this.fromType, this.toType, OperationTypes.UPLOAD);
                metadataList.push(metadata);
                // currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                //     currentRequest.file,
                //     metadata,
                //     uniqueProcessType,
                // ));
                currentRequest.setMetadata(metadata)
            }

            this.notifyFileListChanged();
            request.files.forEach(f => {
                const uploadCompletedFileIndex = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                if (uploadCompletedFileIndex > -1) {
                    request.fakeUploadInProgressFileForSelectedFiles.splice(uploadCompletedFileIndex, 1);
                    this.notifyFileListChanged();
                }
            });

            const promiseArray = [];
            for (let i = 0; i < currentRequestMetadata.length; i++) {
                const fileWithMetadata = currentRequestMetadata[i];
                promiseArray.push(new Promise<void>((resolve, reject) => {
                    const observerObject = {
                        next: x => {
                            fileWithMetadata.setProgress(x);
                            // fileUpload.progress = x;
                        },
                        error: err => {
                            this.logger.error({ message: `Error uploading file (GoogleDrive).`, error: err });
                            fileWithMetadata.setError();
                        },
                        complete: () => {
                            fileWithMetadata.setComplete();
                        },
                    };
                    this.fileUploadService.uploadFileFromGooleDrive(fileWithMetadata, observerObject)
                        .then((originalInputFile: OriginalInputFile) => {
                            fileWithMetadata.setResponse(originalInputFile);
                            this.logger.trace7({ message: `GoogleDrive file upload success.`, data: originalInputFile });
                            resolve();
                        })
                        .catch((e) => reject(e));
                }));
            }
            await Promise.all(promiseArray);
        }
    }

    async multiFileUploadEvent(request: FileSelectHandleRequest<File>) {
        const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
        // resolve for each fle
        // only use first one if it is a merge request, since all files must be on same server

        const currentRequestMetadata: UploadInProgressFile<any>[] = [];
        for (const file of request.files) {
            currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                file,
                null,
                uniqueProcessType,
            ));
        }
        request.fileWithMetadataList.push(...currentRequestMetadata);
        this.notifyFileListChanged();

        for (const currentRequest of currentRequestMetadata) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory['SELECT-DIALOG'],
                GAContants.EventAction['FILE-SELECTED'],
                GAContants.EventLabel.SELECT,
                `file-${currentRequest.file.name}-${currentRequest.file.size}`,
            );
            const metadata = await this.processorResolverService.generateFileUploadRequestMetadata(currentRequest.file, this.fromType, this.toType, OperationTypes.UPLOAD);
            currentRequest.setMetadata(metadata)
        }

        request.files.forEach(f => {
            const uploadCompletedFileIndex = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
            if (uploadCompletedFileIndex > -1) {
                request.fakeUploadInProgressFileForSelectedFiles.splice(uploadCompletedFileIndex, 1);
                this.notifyFileListChanged();
            }
        });

        const promiseArray = [];
        for (let i = 0; i < currentRequestMetadata.length; i++) {
            const fileWithMetadata = currentRequestMetadata[i];
            promiseArray.push(new Promise<void>((resolve, reject) => {
                const observerObject = {
                    next: x => {
                        fileWithMetadata.setProgress(x);
                        // fileUpload.progress = x;
                    },
                    error: err => {
                        this.logger.error({ message: `Error uploading file (Disk_).`, error: err });
                        fileWithMetadata.setError();
                    },
                    complete: () => {
                        fileWithMetadata.setComplete();
                    },
                };
                this.fileUploadService.uploadFileFromLocalDisk(fileWithMetadata, observerObject)
                    .then((originalInputFile: OriginalInputFile) => {
                        fileWithMetadata.setResponse(originalInputFile);
                        this.logger.trace7({ message: `Disk file upload success.`, data: originalInputFile });
                        resolve();
                    })
                    .catch((e) => reject(e));
            }));
        }

        await Promise.all(promiseArray);
    }

}